import styled from '@emotion/styled';
import { Variables } from '../base/variables/variables';

const V = Variables.ThemeLight;

export const Subtitle = styled.p`
    margin: 4px 0 16px;
    font-size: 13px;
    color: ${V.TextMild};
`;

export const Toolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const Filters = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 14px 16px;
    border-bottom: 1px solid ${V.Border1};
`;

export const FilterSlot = styled.div<{ width?: number }>`
    flex: 0 1 ${({ width }) => width || 200}px;
    min-width: 140px;
`;

export const Shown = styled.span`
    margin-left: auto;
    font-size: 12px;
    color: ${V.TextMild};
`;

export const Mono = styled.span`
    font-variant-numeric: tabular-nums;
`;

export const Amount = styled.div<{ strong?: boolean }>`
    width: 100%;
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-weight: ${({ strong }) => (strong ? 600 : 400)};
    color: ${V.TextStrong};
`;

/** The id in blue; the icon says where the remit came from — an electronic ERA in blue, a paper EOB in orange (design 04). */
export const IdCell = styled.div<{ kind: 'ERA' | 'EOB' }>`
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${V.PrimaryConstant6};

    svg {
        flex: none;
        color: ${({ kind }) => (kind === 'EOB' ? V.AttentionConstant6 : V.PrimaryConstant6)};
    }

    /* An EOB's id is a UUID: one line, cut short, whole on hover. */
    span {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

export const Posted = styled.span<{ tone: 'all' | 'some' | 'none' }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
    color: ${({ tone }) => (tone === 'all' ? V.SuccessInverted4 : tone === 'some' ? V.AttentionInverted4 : V.PrimaryConstant6)};

    ::before {
        content: '';
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
    }
`;

export const Muted = styled.span`
    color: ${V.TextMild};
`;

/* ---- The expanded remit -------------------------------------------------------------------- */

export const Detail = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px 24px 24px;
    background: ${V.Background1};
`;

export const MetaRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 16px;
`;

export const Meta = styled.dl`
    flex: 1;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
    gap: 12px 24px;
    margin: 0;

    dt {
        font-size: 12px;
        color: ${V.TextMild};
    }
    dd {
        margin: 2px 0 0;
        font-size: 14px;
        color: ${V.TextStrong};
        overflow-wrap: anywhere;
    }
`;

export const Actions = styled.div`
    display: flex;
    gap: 8px;
    flex: none;
`;

export const Section = styled.section`
    border: 1px solid ${V.Border1};
    border-radius: 8px;
    background: ${V.Background3};
`;

export const SectionHead = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid ${V.Border1};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${V.TextMild};

    span:last-of-type {
        font-weight: 400;
        letter-spacing: 0;
        text-transform: none;
    }
`;

export const Claim = styled.div`
    padding: 14px 16px;
    border-bottom: 1px solid ${V.Border1};

    :last-of-type {
        border-bottom: none;
    }
`;

export const ClaimHead = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 24px;
    margin-bottom: 10px;
    font-size: 13px;
    color: ${V.TextStrong};
`;

export const Label = styled.span`
    margin-right: 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${V.TextMild};
`;

export const ClaimLinks = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 12px;
    margin-left: auto;
`;

export const Lines = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    font-variant-numeric: tabular-nums;

    th {
        padding: 6px 8px;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        text-align: left;
        color: ${V.TextMild};
    }
    td {
        padding: 6px 8px;
        vertical-align: top;
        color: ${V.TextStrong};
    }
    th.num,
    td.num {
        text-align: right;
    }
    tbody tr.line {
        cursor: pointer;
    }
    tbody tr.line:hover td {
        background: ${V.Background1};
    }
    tr.totals td {
        border-top: 1px solid ${V.Border1};
        font-weight: 600;
    }
`;

export const Adjustment = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 12px;
    white-space: nowrap;
`;

export const ClaimFoot = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px 24px;
    margin-top: 8px;
    font-size: 13px;
    color: ${V.TextStrong};
`;

export const Balance = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 6px 24px;
    padding: 12px 16px;
    background: ${V.Background1};
    font-size: 13px;
    font-variant-numeric: tabular-nums;
`;

export const Effect = styled.span<{ sign: number }>`
    font-weight: 600;
    color: ${({ sign }) => (sign > 0 ? V.SuccessInverted4 : sign < 0 ? V.WarningInverted4 : V.TextStrong)};
`;

export const Note = styled.p`
    margin: 0;
    padding: 12px 16px;
    font-size: 13px;
    color: ${V.TextMild};
`;

/* ---- The service-line popup ------------------------------------------------------------------ */

/** Padded itself, as the upload dialog's body is: neither console's dialog host pads its content. */
export const LineDialog = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 520px;
    padding: 0 24px 24px;
`;

export const LineHead = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;

    h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: ${V.TextStrong};
    }
`;

export const LineFacts = styled.dl`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px 24px;
    margin: 0;

    dt {
        font-size: 12px;
        color: ${V.TextMild};
    }
    dd {
        margin: 2px 0 0;
        font-size: 14px;
        color: ${V.TextStrong};
    }
`;

export const AdjustmentCard = styled.div`
    padding: 12px 14px;
    border: 1px solid ${V.Border1};
    border-radius: 8px;

    header {
        display: flex;
        justify-content: space-between;
        font-weight: 600;
    }
    p {
        margin: 6px 0 4px;
        font-size: 13px;
        color: ${V.TextStrong};
    }
    small {
        font-size: 11px;
        letter-spacing: 0.04em;
        color: ${V.TextMild};
    }
`;

/* ---- Filter fields ---------------------------------------------------------------------------- */

export const TableFrame = styled.div`
    width: 100%;
`;

export const NoticeSlot = styled.div`
    padding: 16px;
`;

export const Confirm = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-width: 480px;
    padding: 0 24px 20px;

    p {
        margin: 0;
        font-size: 14px;
        line-height: 1.5;
        color: ${V.TextStrong};
    }
    > div {
        justify-content: flex-end;
    }
`;
